import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  EyeIcon,
  PaperAirplaneIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  getTPRMAssessmentApi,
  getTPRMAssessmentsApi,
  sendTPRMAssessmentApi,
} from "../../../services/axios/AuthApis/TPRMApis";
import DataTable from "../../common/DataTable";
import PageLoader from "../../ui/PageLoader";
import useTPRMDialog from "./useTPRMDialog";

const PAGE_SIZE = 20;
const EMPTY_VALUE = "—";
const STATUS_OPTIONS = [
  "Draft",
  "Sent",
  "In Progress",
  "Under Review",
  "Changes Requested",
  "Completed",
  "Expired",
];

const STATUS_STYLES = {
  Draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
  Sent: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200",
  "In Progress": "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
  "Under Review": "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200",
  "Changes Requested": "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200",
  Completed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
  Expired: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200",
};

const getAssessmentResults = (data) => {
  const results = Array.isArray(data)
    ? data
    : Array.isArray(data?.results)
      ? data.results
      : [];
  const rawCount = data?.count;
  const count =
    rawCount !== null &&
    rawCount !== undefined &&
    rawCount !== "" &&
    Number.isFinite(Number(rawCount))
    ? Number(data.count)
    : results.length;
  return { results, count };
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return EMPTY_VALUE;
  if (typeof value === "object") return EMPTY_VALUE;
  return String(value);
};

const formatDate = (value) => {
  if (value === null || value === undefined || value === "") return EMPTY_VALUE;
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return EMPTY_VALUE;
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: "UTC",
  }).format(parsed);
};

const getAssessmentTemplateName = (assessment) =>
  assessment.template_snapshot?.name ?? EMPTY_VALUE;

const getAssessmentVendorReference = (assessment) =>
  displayValue(assessment.vendor);

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
      STATUS_STYLES[status] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
    }`}
  >
    {displayValue(status)}
  </span>
);

const DetailValue = ({ label, value }) => (
  <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
    <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
    <dd className="mt-1 break-all font-medium text-slate-900 dark:text-white">
      {displayValue(value)}
    </dd>
  </div>
);

const AssessmentDetailsModal = ({ assessmentId, onClose }) => {
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const dialogRef = useTPRMDialog({ onClose });

  useEffect(() => {
    let cancelled = false;
    const loadAssessment = async () => {
      setLoading(true);
      setError("");
      try {
        const result = await getTPRMAssessmentApi(assessmentId);
        if (!cancelled) setAssessment(result);
      } catch (requestError) {
        if (!cancelled) {
          setError(
            requestError.response?.data?.detail ||
              requestError.message ||
              "Unable to load assessment details.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadAssessment();
    return () => {
      cancelled = true;
    };
  }, [assessmentId, reloadKey]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/60 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        aria-labelledby="assessment-details-title"
        aria-modal="true"
        className="relative my-auto max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900 sm:p-6"
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2
              className="text-lg font-semibold text-slate-900 dark:text-white"
              id="assessment-details-title"
            >
              Assessment details
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              {assessmentId}
            </p>
          </div>
          <button
            aria-label="Close assessment details"
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            onClick={onClose}
            title="Close assessment details"
            type="button"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {loading && (
          <p className="text-sm text-slate-600 dark:text-slate-300" role="status">
            Loading assessment details...
          </p>
        )}
        {error && (
          <div className="space-y-3">
            <p className="text-sm text-rose-700 dark:text-rose-300" role="alert">
              {error}
            </p>
            <button
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              onClick={() => setReloadKey((current) => current + 1)}
              type="button"
            >
              Retry loading details
            </button>
          </div>
        )}
        {!loading && !error && assessment && (
          <div className="space-y-5 text-sm">
            <section aria-labelledby="assessment-summary-heading">
              <h3
                className="mb-2 font-semibold text-slate-900 dark:text-white"
                id="assessment-summary-heading"
              >
                Assessment summary
              </h3>
              <dl className="grid grid-cols-1 gap-3 rounded-lg bg-slate-50 p-3 dark:bg-slate-800 sm:grid-cols-2 lg:grid-cols-3">
                <DetailValue label="Assessment ID" value={assessment.assessment_id} />
                <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
                  <dt className="text-slate-500 dark:text-slate-400">Status</dt>
                  <dd className="mt-1">
                    <StatusBadge status={assessment.status} />
                  </dd>
                </div>
                <DetailValue label="Vendor" value={assessment.vendor_name || getAssessmentVendorReference(assessment)} />
                <DetailValue label="Vendor ID" value={getAssessmentVendorReference(assessment)} />
                <DetailValue label="Service" value={assessment.service_name || assessment.service_id} />
                <DetailValue label="Questionnaire" value={getAssessmentTemplateName(assessment)} />
                <DetailValue label="Assessment type" value={assessment.assessment_type} />
                <DetailValue label="Recipient" value={assessment.recipient_email} />
                <DetailValue label="Overall score" value={assessment.overall_score} />
              </dl>
            </section>
            <section aria-labelledby="assessment-timeline-heading">
              <h3
                className="mb-2 font-semibold text-slate-900 dark:text-white"
                id="assessment-timeline-heading"
              >
                Timeline and review
              </h3>
              <dl className="grid grid-cols-1 gap-3 rounded-lg bg-slate-50 p-3 dark:bg-slate-800 sm:grid-cols-2 lg:grid-cols-3">
                <DetailValue label="Created date" value={formatDate(assessment.created_at)} />
                <DetailValue label="Sent date" value={formatDate(assessment.sent_date)} />
                <DetailValue label="Due date" value={formatDate(assessment.due_date)} />
                <DetailValue label="Completed date" value={formatDate(assessment.completed_date)} />
                <DetailValue label="Reviewer" value={assessment.reviewer} />
                <DetailValue label="Reviewed date" value={formatDate(assessment.reviewed_at)} />
                <DetailValue label="Reviewer comments" value={assessment.reviewer_comments} />
              </dl>
            </section>
          </div>
        )}
        {!loading && !error && !assessment && (
          <p className="text-sm text-slate-600 dark:text-slate-300" role="status">
            No assessment details were returned.
          </p>
        )}
        <div className="mt-5 flex justify-end">
          <button
            className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
            onClick={onClose}
            type="button"
          >
            Close
          </button>
        </div>
      </section>
    </div>
  );
};

const SendAssessmentDialog = ({ assessment, onClose, onSent }) => {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const dialogRef = useTPRMDialog({
    onClose,
    closeOnEscape: !sending,
  });

  const send = async () => {
    setSending(true);
    setError("");
    try {
      await sendTPRMAssessmentApi(assessment.assessment_id);
      onSent();
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail ||
          requestError.message ||
          "Unable to send the assessment.",
      );
    } finally {
      setSending(false);
    }
  };

  const openAssessment = () => {
    const url = new URL("/ai-tprm", window.location.origin);
    url.searchParams.set("tab", "assessments");
    url.searchParams.set("assessment_id", assessment.assessment_id);
    window.open(url.toString(), "_blank", "noopener,noreferrer");
    onClose();
  };
  const canSend = assessment.status === "Draft";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
      <section
        aria-labelledby="send-assessment-title"
        aria-modal="true"
        className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-700 dark:bg-slate-900"
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <h2
          id="send-assessment-title"
          className="text-lg font-semibold text-slate-900 dark:text-white"
        >
          Assessment delivery and response entry
        </h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Send the questionnaire invitation to the vendor, or open the
          assessment to review or enter responses.
        </p>
        <dl className="mt-4 space-y-3 rounded-lg bg-slate-50 p-4 text-sm dark:bg-slate-800">
          <DetailValue
            label="Vendor"
            value={assessment.vendor_name || assessment.vendor}
          />
          <DetailValue
            label="Recipient email"
            value={assessment.recipient_email}
          />
          <DetailValue
            label="Questionnaire"
            value={assessment.template_snapshot?.name}
          />
          <DetailValue
            label="Due date"
            value={formatDate(assessment.due_date)}
          />
        </dl>
        {error && (
          <p
            className="mt-4 text-sm text-rose-700 dark:text-rose-300"
            role="alert"
          >
            {error}
          </p>
        )}
        <div className="mt-5 flex flex-wrap justify-end gap-3">
          <button
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-60 dark:border-slate-700 dark:text-slate-200"
            disabled={sending}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            aria-label={`Open assessment questionnaire for ${assessment.assessment_id}`}
            className="rounded-lg bg-indigo-700 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-800 disabled:opacity-60"
            disabled={sending || !assessment.assessment_id}
            onClick={openAssessment}
            type="button"
          >
            Open Assessment
          </button>
          <button
            aria-label={`Send assessment invitation for ${assessment.assessment_id}`}
            className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={
              sending ||
              !canSend ||
              !assessment.assessment_id ||
              !assessment.recipient_email ||
              !assessment.template_snapshot?.name
            }
            onClick={send}
            type="button"
          >
            {sending ? "Sending..." : "Send"}
          </button>
        </div>
        {!canSend && (
          <p className="mt-3 text-right text-xs text-slate-500 dark:text-slate-400">
            Only draft assessments can be sent.
          </p>
        )}
      </section>
    </div>
  );
};

const AssessmentsPage = ({ user }) => {
  const [assessments, setAssessments] = useState([]);
  const [totalAssessments, setTotalAssessments] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [columnFilters, setColumnFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState(null);
  const [assessmentToSend, setAssessmentToSend] = useState(null);
  const [sendSuccess, setSendSuccess] = useState("");
  const canSendAssessment = [
    "admin",
    "platform_admin",
    "superadmin",
    "customer_admin",
  ].includes(String(user?.role || "").toLowerCase());
  const filterValues = useMemo(
    () => Object.fromEntries(columnFilters.map(({ id, value }) => [id, value])),
    [columnFilters],
  );

  useEffect(() => {
    let cancelled = false;
    const loadAssessments = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getTPRMAssessmentsApi({
          ...filterValues,
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
        });
        const result = getAssessmentResults(data);
        if (!cancelled) {
          setAssessments(result.results);
          setTotalAssessments(result.count);
          setHasLoaded(true);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(
            requestError.response?.data?.detail ||
              requestError.message ||
              "Unable to load assessments.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadAssessments();
    return () => {
      cancelled = true;
    };
  }, [filterValues, pagination.pageIndex, pagination.pageSize, reloadKey]);

  const columns = useMemo(
    () => [
      {
        accessorFn: (assessment) => assessment.assessment_id ?? EMPTY_VALUE,
        id: "assessment_id",
        header: "Assessment ID",
        cell: ({ getValue }) => displayValue(getValue()),
        size: 190,
      },
      {
        accessorFn: getAssessmentVendorReference,
        id: "vendor_id",
        header: "Vendor",
        cell: ({ getValue }) => displayValue(getValue()),
        size: 180,
        meta: {
          filter: {
            variant: "text",
            label: "Vendor ID",
            placeholder: "Enter exact vendor ID",
          },
        },
      },
      {
        accessorFn: getAssessmentTemplateName,
        id: "template",
        header: "Questionnaire / template",
        cell: ({ getValue }) => displayValue(getValue()),
        size: 190,
      },
      {
        accessorKey: "assessment_type",
        header: "Assessment type",
        cell: ({ getValue }) => displayValue(getValue()),
        size: 160,
      },
      {
        accessorKey: "created_at",
        header: "Created date",
        cell: ({ getValue }) => formatDate(getValue()),
        size: 140,
      },
      {
        accessorKey: "due_date",
        header: "Due date",
        cell: ({ getValue }) => formatDate(getValue()),
        size: 140,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => <StatusBadge status={getValue()} />,
        size: 155,
        meta: {
          filter: {
            variant: "select",
            label: "Assessment status",
            options: STATUS_OPTIONS.map((value) => ({ value, label: value })),
          },
        },
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        cell: ({ row }) => {
          const id = row.original.assessment_id;
          return (
            <div className="flex items-center gap-2">
            {canSendAssessment && (
              <button
                aria-label={`Send assessment ${displayValue(id)}`}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900 dark:text-emerald-300 dark:hover:bg-slate-800"
                disabled={id === null || id === undefined || id === ""}
                onClick={() => {
                  setSendSuccess("");
                  setAssessmentToSend(row.original);
                }}
                type="button"
              >
                <PaperAirplaneIcon aria-hidden="true" className="h-4 w-4" />
                <span>Send</span>
              </button>
            )}
            <button
              aria-label={`View assessment ${displayValue(id)}`}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 dark:border-blue-900 dark:text-blue-300 dark:hover:bg-slate-800"
              disabled={id === null || id === undefined || id === ""}
              onClick={() => setSelectedAssessmentId(String(id))}
              type="button"
            >
              <EyeIcon className="h-4 w-4" />

            </button>
            </div>
          );
        },
        size: 105,
      },
    ],
    [canSendAssessment],
  );

  const handlePaginationChange = useCallback((updater) => {
    setPagination((current) =>
      typeof updater === "function" ? updater(current) : updater,
    );
  }, []);

  const retry = () => setReloadKey((current) => current + 1);
  const pageCount = Math.ceil(totalAssessments / pagination.pageSize);

  if (loading && !hasLoaded) {
    return <PageLoader title="Loading assessments..." minH="min-h-[40vh]" />;
  }

  if (error) {
    return (
      <section
        className="rounded-xl border border-rose-200 bg-white p-6 dark:border-rose-900 dark:bg-slate-900"
        role="alert"
      >
        <h2 className="font-semibold text-slate-900 dark:text-white">
          Assessments could not be loaded
        </h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{error}</p>
        <button
          type="button"
          onClick={retry}
          className="mt-4 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          Try again
        </button>
      </section>
    );
  }

  return (
    <>
      <section
        aria-labelledby="tprm-assessments-heading"
        className="space-y-4"
      >
        <div>
          <h2
            className="text-lg font-semibold text-slate-900 dark:text-white"
            id="tprm-assessments-heading"
          >
            Assessments
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Assessments in your authorized TPRM scope.
          </p>
        </div>
        {sendSuccess && (
          <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200" role="status">
            {sendSuccess}
          </p>
        )}
        <DataTable
          columns={columns}
          data={assessments}
          loading={loading}
          error={null}
          emptyMessage="No assessments found."
          enableSorting={false}
          enableGlobalFilter={false}
          enableColumnFilters
          enableColumnVisibility={false}
          manualPagination
          manualFiltering
          columnFilters={columnFilters}
          onColumnFiltersChange={(updater) => {
            const next = typeof updater === "function" ? updater(columnFilters) : updater;
            setColumnFilters(next);
            setPagination((current) => ({ ...current, pageIndex: 0 }));
          }}
          rowCount={totalAssessments}
          pageCount={pageCount}
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          getRowId={(assessment, index) =>
            String(assessment.assessment_id ?? assessment.id ?? index)
          }
          className="min-w-0 [&_.overflow-x-auto]:dark:border-slate-700 [&_.overflow-x-auto]:dark:bg-slate-900 [&_thead]:dark:bg-slate-800 [&_thead]:dark:text-slate-300 [&_th]:dark:border-slate-700 [&_tbody]:dark:divide-slate-800 [&_tbody_tr]:dark:hover:bg-slate-800 [&_tbody_td]:dark:text-slate-300 [&_td]:dark:border-slate-800 [&_tfoot]:dark:bg-slate-900 [&_tfoot]:dark:text-slate-300"
        />
      </section>
      {selectedAssessmentId && (
        <AssessmentDetailsModal
          assessmentId={selectedAssessmentId}
          onClose={() => setSelectedAssessmentId(null)}
        />
      )}
      {assessmentToSend && (
        <SendAssessmentDialog
          assessment={assessmentToSend}
          onClose={() => setAssessmentToSend(null)}
          onSent={() => {
            setAssessmentToSend(null);
            setSendSuccess("Assessment invitation email was initiated successfully.");
            setReloadKey((current) => current + 1);
          }}
        />
      )}
    </>
  );
};

export default AssessmentsPage;
